import { useMemo } from 'react';

import { isRowApproved } from './approvalQueueModel';
import type { ChecklistStatus } from './shared/checklistUtils';
import type { BlackoutChecklistRow } from './tabs/blackoutDates/types';
import type { BundledExperienceChecklistRow } from './tabs/bundledExperiences/types';
import type { CampaignChecklistRow } from './tabs/campaign/types';
import type { DealOptionsInclusionsChecklistRow } from './tabs/dealOptionsInclusions/types';
import type { DiscountsMarginChecklistRow } from './tabs/discountsMargin/types';
import type { EgsCapacityChecklistRow, EgsMarginChecklistRow } from './tabs/egs/types';
import type { SetupChecklistRow } from './tabs/setup/types';
import type { SurchargeTableRow, SurchargesChecklistRow } from './tabs/surcharges/types';
import type { TaxesPaymentsChecklistRow } from './tabs/taxesPayments/types';
import type { ChecklistCategoryId, ManualOverrideState } from './types';

function countNeedsApproval(
  rows: Array<{ id: string; status: ChecklistStatus }>,
  manualOverrides: ManualOverrideState,
) {
  return rows.filter((row) => row.status !== 'blocked' && !isRowApproved(row, manualOverrides)).length;
}

export function useChecklistSummary({
  baseSurchargesRows,
  campaignRows,
  discountsMarginRows,
  blackoutRows,
  bundledExperienceRows,
  taxesPaymentsRows,
  capacityRows,
  dealOptionsInclusionsRows,
  manualOverrides,
  marginRows,
  setupRows,
  surchargeTableRows,
}: {
  baseSurchargesRows: Array<SurchargesChecklistRow>;
  campaignRows: Array<CampaignChecklistRow>;
  discountsMarginRows: Array<DiscountsMarginChecklistRow>;
  blackoutRows: Array<BlackoutChecklistRow>;
  bundledExperienceRows: Array<BundledExperienceChecklistRow>;
  taxesPaymentsRows: Array<TaxesPaymentsChecklistRow>;
  capacityRows: Array<EgsCapacityChecklistRow>;
  dealOptionsInclusionsRows: Array<DealOptionsInclusionsChecklistRow>;
  manualOverrides: ManualOverrideState;
  marginRows: Array<EgsMarginChecklistRow>;
  setupRows: Array<SetupChecklistRow>;
  surchargeTableRows: Array<SurchargeTableRow>;
}) {
  return useMemo(() => {
    const allRows = [
      ...setupRows,
      ...campaignRows,
      ...discountsMarginRows,
      ...bundledExperienceRows,
      ...marginRows,
      ...capacityRows,
      ...dealOptionsInclusionsRows,
      ...blackoutRows,
      ...taxesPaymentsRows,
      ...baseSurchargesRows,
      ...surchargeTableRows,
    ];
    const approvedCount = allRows.filter((row) => isRowApproved(row, manualOverrides)).length;
    const blockedCount = allRows.filter((row) => row.status === 'blocked').length;
    const totalCount = allRows.length;
    const needsApprovalCount = totalCount - approvedCount - blockedCount;

    const categoryCounts: Record<ChecklistCategoryId, number> = {
      setup: countNeedsApproval(setupRows, manualOverrides),
      campaign: countNeedsApproval(campaignRows, manualOverrides),
      discountsMargin: countNeedsApproval(discountsMarginRows, manualOverrides),
      taxesPayments: countNeedsApproval(taxesPaymentsRows, manualOverrides),
      dealOptionsInclusions: countNeedsApproval(dealOptionsInclusionsRows, manualOverrides),
      surcharges:
        countNeedsApproval(baseSurchargesRows, manualOverrides) +
        countNeedsApproval(surchargeTableRows, manualOverrides),
      blackoutDates: countNeedsApproval(blackoutRows, manualOverrides),
      egs: countNeedsApproval(marginRows, manualOverrides) + countNeedsApproval(capacityRows, manualOverrides),
      bundledExperiences: countNeedsApproval(bundledExperienceRows, manualOverrides),
    };

    return {
      approvedCount,
      categoryCounts,
      needsApprovalCount,
      totalCount,
    };
  }, [
    baseSurchargesRows,
    campaignRows,
    discountsMarginRows,
    blackoutRows,
    bundledExperienceRows,
    taxesPaymentsRows,
    capacityRows,
    dealOptionsInclusionsRows,
    manualOverrides,
    marginRows,
    setupRows,
    surchargeTableRows,
  ]);
}
